import { cloneDeep } from 'lodash'
import { VegaSpec } from '../../../model/StatsAssistant'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const SAFE_FIELD_NAME = /^[a-zA-Z_][a-zA-Z0-9_]*$/

const needsFieldSanitize = (fieldName: string): boolean => !SAFE_FIELD_NAME.test(fieldName)

const toSafeFieldName = (fieldName: string, used: Set<string>): string => {
  const base =
    fieldName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'field'

  let candidate = base
  let suffix = 2
  while (used.has(candidate)) {
    candidate = `${base}_${suffix++}`
  }
  used.add(candidate)

  return candidate
}

const collectEncodingFieldNames = (node: unknown, names: Set<string>): void => {
  if (Array.isArray(node)) {
    node.forEach((item) => collectEncodingFieldNames(item, names))
    return
  }
  if (!isRecord(node)) return

  if (typeof node.field === 'string') {
    names.add(node.field)
  }

  Object.values(node).forEach((value) => collectEncodingFieldNames(value, names))
}

const renameEncodingFields = (node: unknown, mapping: Map<string, string>): void => {
  if (Array.isArray(node)) {
    node.forEach((item) => renameEncodingFields(item, mapping))
    return
  }
  if (!isRecord(node)) return

  if (typeof node.field === 'string' && mapping.has(node.field)) {
    node.field = mapping.get(node.field)
  }

  Object.values(node).forEach((value) => renameEncodingFields(value, mapping))
}

const renameDatasetRows = (
  rows: unknown[],
  mapping: Map<string, string>,
): Record<string, unknown>[] =>
  rows.map((row) => {
    if (!isRecord(row)) return {}

    const renamed: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(row)) {
      renamed[mapping.get(key) ?? key] = value
    }

    return renamed
  })

/** Looker CA génère des noms de colonnes FR avec espaces/apostrophes — invalides en access path Vega-Lite. */
const sanitizeLookerFieldNames = (spec: Record<string, unknown>): void => {
  const fieldNames = new Set<string>()
  collectEncodingFieldNames(spec.encoding, fieldNames)

  if (isRecord(spec.datasets)) {
    for (const dataset of Object.values(spec.datasets)) {
      if (!Array.isArray(dataset)) continue
      for (const row of dataset) {
        if (isRecord(row)) {
          Object.keys(row).forEach((key) => fieldNames.add(key))
        }
      }
    }
  }

  if (Array.isArray(spec.data) || (isRecord(spec.data) && Array.isArray(spec.data.values))) {
    const values = Array.isArray(spec.data) ? spec.data : spec.data.values
    if (Array.isArray(values)) {
      for (const row of values) {
        if (isRecord(row)) {
          Object.keys(row).forEach((key) => fieldNames.add(key))
        }
      }
    }
  }

  const mapping = new Map<string, string>()
  const usedSafeNames = new Set<string>()

  for (const fieldName of fieldNames) {
    if (!needsFieldSanitize(fieldName)) continue
    mapping.set(fieldName, toSafeFieldName(fieldName, usedSafeNames))
  }

  if (mapping.size === 0) return

  if (isRecord(spec.datasets)) {
    for (const [datasetName, dataset] of Object.entries(spec.datasets)) {
      if (Array.isArray(dataset)) {
        spec.datasets[datasetName] = renameDatasetRows(dataset, mapping)
      }
    }
  }

  if (isRecord(spec.data) && Array.isArray(spec.data.values)) {
    spec.data.values = renameDatasetRows(spec.data.values, mapping)
  }

  renameEncodingFields(spec.encoding, mapping)
}

const inlineNamedDataset = (spec: Record<string, unknown>): void => {
  if (!isRecord(spec.data) || typeof spec.data.name !== 'string') return
  if (!isRecord(spec.datasets)) return

  const dataset = spec.datasets[spec.data.name]
  if (!Array.isArray(dataset)) return

  spec.data = { values: dataset }
  delete spec.datasets
}

export const isVegaLiteSpec = (spec: VegaSpec): boolean => {
  const schema = typeof spec.$schema === 'string' ? spec.$schema : ''
  if (schema.includes('vega-lite')) {
    return true
  }

  return 'mark' in spec && 'encoding' in spec
}

export const prepareVegaSpec = (spec: VegaSpec, width: number, height: number): VegaSpec => {
  const prepared = cloneDeep(spec) as Record<string, unknown>

  if (isVegaLiteSpec(spec)) {
    sanitizeLookerFieldNames(prepared)
    inlineNamedDataset(prepared)

    prepared.width = width
    prepared.height = height
    prepared.autosize = { type: 'fit', contains: 'padding', resize: true }

    const config = isRecord(prepared.config) ? prepared.config : {}
    prepared.config = {
      ...config,
      view: { stroke: null, ...(isRecord(config.view) ? config.view : {}) },
      axis: {
        labelFontSize: 11,
        titleFontSize: 12,
        ...(isRecord(config.axis) ? config.axis : {}),
      },
      axisX: {
        labelAngle: -25,
        labelAlign: 'right',
        labelBaseline: 'middle',
        ...(isRecord(config.axisX) ? config.axisX : {}),
      },
      legend: {
        orient: 'top',
        labelFontSize: 11,
        ...(isRecord(config.legend) ? config.legend : {}),
      },
    }

    if (isRecord(prepared.encoding)) {
      const x = prepared.encoding.x
      if (isRecord(x)) {
        const axis = isRecord(x.axis) ? x.axis : {}
        if (axis.labelAngle === 90 || axis.labelAngle === -90) {
          prepared.encoding = {
            ...prepared.encoding,
            x: { ...x, axis: { ...axis, labelAngle: -25, labelAlign: 'right' } },
          }
        }
      }
    }

    return prepared as VegaSpec
  }

  // Spec Vega complète (format Looker CA) — ne pas injecter autosize/encoding Vega-Lite.
  if (!prepared.width) {
    prepared.width = width
  }
  if (!prepared.height) {
    prepared.height = height
  }

  return prepared as VegaSpec
}

export const getChartDimensions = (
  containerWidth: number,
  compact: boolean,
): { width: number; height: number } => {
  const effectiveWidth = containerWidth > 0 ? containerWidth : compact ? 360 : 720
  const width = Math.max(Math.floor(effectiveWidth), compact ? 260 : 320)
  const height = compact ? 240 : Math.min(420, Math.round(width * 0.55))

  return { width, height }
}
